<template>
  <div v-if="showHeadlineArea" class="content-center">
    <div class="flex items-center gap-2">
      <h1 class="h1">
        <span v-if="!dynamicTitle" class="flex items-center">
          <span :class="['h1-icon', 'pi pi-spin pi-spinner']"></span
          ><span class="h1-text" data-testid="page-headline"> ... </span>
        </span>
        <span v-else class="flex items-center"
          ><span :class="['h1-icon', icon]"></span
          ><span class="h1-text" data-testid="page-headline">{{ dynamicTitle }}</span></span
        >
      </h1>
      <span v-if="dynamicSubitle" class="h1">•</span>
      <h2 v-if="dynamicSubitle" class="h2">
        <span class="flex items-center" data-testid="page-subheadline">{{ dynamicSubitle }}</span>
      </h2>
    </div>
  </div>
  <div v-if="!viewportStore.isMobile" class="flex">
    <Menubar v-if="!isSplitPanel" class="menu-bar-content" :model="items">
      <template #item="{ item, props: slotProps, hasSubmenu, root }">
        <a v-ripple class="flex items-center gap-2" v-bind="slotProps.action">
          <span :class="item.icon"></span>
          <span
            v-tooltip="{
              value: item.tooltip,
              fitContent: true,
              showDelay: 300,
            }"
            class="font-bold"
            >{{ item.label }}</span
          >
          <i
            v-if="hasSubmenu"
            :class="[
              'pi pi-angle-down ml-auto',
              { 'pi-angle-down': root, 'pi-angle-right': !root },
            ]"
          ></i>
        </a>
      </template>
      <template #end>
        <div class="flex items-center gap-2">
          <LanguageSelector />
          <ThemeToggle />
        </div>
      </template>
    </Menubar>
  </div>
</template>

<script setup lang="ts">
import { computed, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import Menubar from 'primevue/menubar';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { useLogout } from '@/composables/useLogout';
import { type PageTranslations, useTranslationByRoute } from '@/composables/useTranslation';
import { useContentModeStore } from '@/stores/contentmode';
import { useHeadlineStore } from '@/stores/headline';
import { useProfileStore } from '@/stores/profile';
import { useViewportStore } from '@/stores/viewport';
import { findNavigationItem } from '@/router/navigation';

import LanguageSelector from '../../common-components/LanguageSelector.vue';
import ThemeToggle from '../../common-components/ThemeToggle.vue';

const profileStore = useProfileStore();
const headlineStore = useHeadlineStore();
const viewportStore = useViewportStore();
const headline = computed(() => headlineStore.headline);
const subheadline = computed(() => headlineStore.subheadline);

const route = useRoute();
const { t } = useI18n();
const contentModeStore = useContentModeStore();
const { logout } = useLogout();
const isSplitView = computed(() => contentModeStore.isSplitViewActive);
const routePath = computed(() => route.path);
const translations = useTranslationByRoute({
  subbranch: 'pages',
  routePath: routePath,
}) as Ref<PageTranslations>;
const { userProfile: user } = storeToRefs(profileStore);

const title = computed(() => translations.value.title);

const mainComponentKey = computed<string>(() => {
  const p = route.params?.mainComponent;
  const pStr = Array.isArray(p) ? p[0] : p;
  const name = route.name;
  const nameStr = typeof name === 'string' ? name : String(name ?? '');
  return pStr ?? nameStr;
});

const icon = computed(() => findNavigationItem(mainComponentKey.value)?.icon || 'pi pi-box');

const items = computed(() => [
  {
    label: user.value?.userName ?? '',
    icon: 'pi pi-user',
    tooltip: user.value?.email ?? null,
    items: [
      {
        label: t('navigation.logout'),
        icon: 'pi pi-sign-out',
        command: () => {
          logout();
        },
      },
    ],
  },
]);

const props = defineProps({
  isSplitPanel: {
    type: Boolean,
    default: false,
  },
});

const showHeadlineArea = computed(() => {
  // In split view, the right panel should not render any headline area.
  return !(isSplitView.value && props.isSplitPanel);
});

const dynamicTitle = computed(() => {
  if (isSplitView.value && props.isSplitPanel) {
    return undefined;
  }

  // Left split panel should follow the same logic as standard view.
  return headline.value ?? title.value; // default view
});

const dynamicSubitle = computed(() => {
  if (isSplitView.value && props.isSplitPanel) {
    return undefined;
  }

  return subheadline.value;
});
</script>

<style scoped lang="css">
@config "../../../../../tailwind.config.js";
@reference "tailwindcss";
@reference "tailwindcss/utilities";

@layer app {
  .h1 {
    @apply flex my-4 px-0 pt-2 text-3xl font-normal;
  }

  .h1-icon {
    @apply text-4xl text-iconred-500 relative;
  }

  .h1-text {
    @apply relative pl-4;
  }

  .h2 {
    @apply flex px-0 pt-2 text-3xl font-normal;
  }

  .menu-bar-content.p-menubar {
    width: auto;
    background: transparent;
    @apply m-0 p-0 border-none;
  }
}
</style>
